<script lang="ts" setup>
import { SizeTypeLabel, TechDataVariations, TechDataVehicle } from '@/types';
import { useVehicleSelection } from '@/composables/useVehicleSelection';
import { getVehicleType } from '@/utils/vehicleType';
import { useTranslations } from '@/composables/useTranslations';
import { useTracking } from '@/composables/useTracking';
import { computed, watch, toRef } from 'vue';
import TechDataTagGroup from './tech-data-tag-group.vue';

const props = defineProps<{
  sizes: SizeTypeLabel[];
  validOptions: {
    weights: number[] | null;
    roofs: string[] | null;
    cabins: string[] | null;
    drivetrains: string[] | null;
    batteryCapacities: number[] | null;
    engines: string[] | null;
  };
  variations: TechDataVariations;
  vehicles: TechDataVehicle[];
  translations: Record<string, string>;
  modelSeries: string;
  selectedVariant?: TechDataVehicle;
}>();

const emit = defineEmits(['variant-selected']);

const vehicleType: 'Citan' | 'Vito' | 'Sprinter_Cabin' | 'Sprinter_Roof' =
  getVehicleType(props.variations) ?? 'Sprinter_Cabin';

const {
  vehicle,
  available_weights,
  available_cabins,
  available_roofs,
  available_engines,
  available_batteryCapacities,
  available_drivetrains,
  selectCabin,
  selectRoof,
  selectEngine,
  selectBatteryCapacity,
  selectWeight,
  selectDrivetrain,
  setVehiclePropertiesBySize,
} = useVehicleSelection(
  vehicleType,
  props.variations,
  props.vehicles,
  props.selectedVariant,
);

const { trackFeatureSelection } = useTracking(props.modelSeries);
const {
  getSizeTypeTranslation,
  getRoofTypeTranslation,
  getCabinTypeTranslation,
  getDrivetrainTranslation,
  getEngineTypeTranslation,
  getComponentLabel,
} = useTranslations(toRef(props, 'translations'));

const selectedVehicle = computed(() => vehicle.value);
const selectedSize = computed(() => vehicle.value?.sizeType);

function isSizeSelected(item: { sizeType: string }) {
  return item.sizeType === selectedSize.value;
}

function radioAttrs(selected: boolean, disabled = false) {
  return {
    role: 'radio',
    'aria-checked': String(selected),
    'aria-disabled': String(disabled),
    tabindex: selected && !disabled ? 0 : -1,
    checked: selected || undefined,
  };
}

watch(
  selectedVehicle,
  (newVehicle) => {
    emit('variant-selected', newVehicle);
  },
  { immediate: true },
);
</script>

<template>
  <div class="owvans-technical-data-wrapper">
    <div v-if="variations.variations.length > 1">
      <span
        id="sizeTypeLabel"
        class="owvans-technical-data-wrapper__column-text wb-text-s"
      >
        {{ getComponentLabel('owvc_tech_data_sizeTypeLabel', 'sizeTypeLabel') }}
      </span>
      <div
        class="owvans-technical-data-tag-group"
        role="radiogroup"
        aria-labelledby="sizeTypeLabel"
      >
        <wb7-interactive-tag
          v-for="(variationItem, index) in variations.variations"
          :key="`size-button${index}`"
          type="tertiary"
          variant="radio"
          v-bind="radioAttrs(isSizeSelected(variationItem))"
          aria-disabled="false"
          :data-test-id="`owvans-tech-data-variations-desktop-${variationItem.sizeType}`"
          @click="
            setVehiclePropertiesBySize(variationItem);
            trackFeatureSelection(
              `size_type.${variationItem.sizeType}`,
              vehicle ?? null,
            );
          "
          @keydown.enter.prevent="
            setVehiclePropertiesBySize(variationItem);
            trackFeatureSelection(
              `size_type.${variationItem.sizeType}`,
              vehicle ?? null,
            );
          "
          size="m"
        >
          {{ getSizeTypeTranslation(variationItem.sizeType) }}
        </wb7-interactive-tag>
      </div>
    </div>

    <TechDataTagGroup
      name="roofs"
      :label="
        getComponentLabel('owvc_tech_data_roofTypeLabel', 'roofTypeLabel')
      "
      v-if="validOptions.roofs"
      :options="validOptions.roofs"
      :selected="vehicle?.roofHeightType"
      :available="available_roofs"
      :getOptionLabel="getRoofTypeTranslation"
      :onSelect="selectRoof"
      :onTrack="trackFeatureSelection"
      trackPrefix="roof_type"
      :vehicle="vehicle"
    />

    <TechDataTagGroup
      name="cabins"
      :label="
        getComponentLabel('owvc_tech_data_cabinTypeLabel', 'cabinTypeLabel')
      "
      v-if="validOptions.cabins"
      :options="validOptions.cabins"
      :selected="vehicle?.cabinType"
      :available="available_cabins"
      :getOptionLabel="getCabinTypeTranslation"
      :onSelect="selectCabin"
      :onTrack="trackFeatureSelection"
      trackPrefix="cabin_type"
      :vehicle="vehicle"
    />

    <TechDataTagGroup
      name="engines"
      :label="getComponentLabel('owvc_tech_data_engine', 'engine')"
      v-if="validOptions.engines"
      :options="validOptions.engines"
      :selected="vehicle?.engineType"
      :available="available_engines"
      :getOptionLabel="getEngineTypeTranslation"
      :onSelect="selectEngine"
      :onTrack="trackFeatureSelection"
      trackPrefix="engine_type"
      :vehicle="vehicle"
    />

    <TechDataTagGroup
      name="batteryCapacity"
      :label="
        getComponentLabel('owvc_tech_data_batteryCapacity', 'batteryCapacity')
      "
      v-if="validOptions.batteryCapacities"
      :options="validOptions.batteryCapacities"
      :selected="vehicle?.batteryCapacity"
      :available="available_batteryCapacities"
      :getOptionLabel="(v) => v"
      :onSelect="selectBatteryCapacity"
      :onTrack="trackFeatureSelection"
      trackPrefix="battery_capacity"
      :vehicle="vehicle"
    />

    <TechDataTagGroup
      name="weights"
      :label="getComponentLabel('owvc_tech_data_weight', 'weight')"
      v-if="validOptions.weights"
      :options="validOptions.weights"
      :selected="vehicle?.weights?.weightMax?.value"
      :available="available_weights"
      :getOptionLabel="(v) => v"
      :onSelect="selectWeight"
      :onTrack="trackFeatureSelection"
      trackPrefix="weight"
      :vehicle="vehicle"
    />

    <TechDataTagGroup
      name="drivetrains"
      :label="
        getComponentLabel('owvc_tech_data_drivetrainType', 'drivetrainType')
      "
      v-if="validOptions.drivetrains"
      :options="validOptions.drivetrains"
      :selected="vehicle?.drivetrainType"
      :available="available_drivetrains"
      :getOptionLabel="getDrivetrainTranslation"
      :onSelect="selectDrivetrain"
      :onTrack="trackFeatureSelection"
      trackPrefix="drivetrain"
      :vehicle="vehicle"
    />
  </div>
</template>

<style lang="scss" scoped>
@import '@workbench/core/dist/scss/utility.scss';
@import '@workbench/core/dist/css/debuggers/media-query.css';
.owvans-technical-data-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 64px;
  margin-top: 32px;
  margin-bottom: 32px;

  &__column-text {
    color: var(--wb-grey-20);
    display: -ms-flexbox;
    font-family: var(--wb-font-text);
    font-size: 1rem;
    font-style: normal;
    font-weight: 700;
  }
}
.owvans-technical-data-tag-group {
  display: flex;
  margin-top: 8px;
  gap: 12px;
}
.wb-radio-group,
.wbx-radio-group {
  white-space: nowrap;
}
</style>
